import Link from 'next/link';
import { IShoppingCartProduct } from '@monofront/types';
import { useState, memo } from 'react';
import NotifyMeButton from '@components/NotifyMeButton';
import ButtonRemoveProductFromShoppingCart from './ButtonRemoveProductFromShoppingCart';
import OOSProductImage from './OOSProductImage';
import { useTranslations } from 'next-intl';
import Replace from '@monofront/design-system/visuals/Icons/Old/Replace';
import Button from '@monofront/design-system/components/Button';

interface IShoppingCartOOSProductRowProps {
  shoppingCartOOSProduct: IShoppingCartProduct;
  onOpenModal: (shoppingCartOOSProduct: IShoppingCartProduct) => void;
}

const ShoppingCartOOSProductRow: React.FC<IShoppingCartOOSProductRowProps> = ({
  shoppingCartOOSProduct,
  onOpenModal,
}) => {
  const tProducts = useTranslations('Products');
  const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false);
  const productLink = shoppingCartOOSProduct.handle
    ? '/products/' + shoppingCartOOSProduct.handle
    : '/';

  return (
    <>
      <div className="oos-products-container">
        <div className="img-and-content">
          <div className="img">
            <OOSProductImage
              handle={shoppingCartOOSProduct.handle}
              img={shoppingCartOOSProduct.image}
            />
          </div>
          <div className="oos-product-content">
            <Link href={productLink} legacyBehavior>
              <a>
                <span className="title hxrt">
                  {shoppingCartOOSProduct.title}
                </span>
              </a>
            </Link>
            <span className="quantity hxrt">
              {tProducts('quantity')} : {shoppingCartOOSProduct.quantity}
            </span>
          </div>
        </div>
        <div
          className="actions"
          data-replacement-source={
            shoppingCartOOSProduct.replacementProduct?.replacementSource
          }
        >
          <div className="action">
            {shoppingCartOOSProduct.replacementProduct ? (
              <Button
                hasMinWidth={false}
                onClick={() => onOpenModal(shoppingCartOOSProduct)}
              >
                <Replace />
                {tProducts('replace')}
              </Button>
            ) : (
              <NotifyMeButton
                productTitle={shoppingCartOOSProduct.title}
                sku={shoppingCartOOSProduct.sku}
                productTags={shoppingCartOOSProduct.tags}
                inventoryAvailable={shoppingCartOOSProduct.inventory_quantity}
                isNotifyMeModalOpen={isNotifyMeModalOpen}
                setIsNotifyMeModalOpen={setIsNotifyMeModalOpen}
                hasIcon
              />
            )}
          </div>
          <div className="remove-product">
            <ButtonRemoveProductFromShoppingCart
              product={shoppingCartOOSProduct}
            />
          </div>
        </div>
      </div>
      <style jsx>{`
        .oos-products-container,
        .img-and-content,
        .actions {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        .oos-products-container {
          padding: var(--space-4) 0 var(--space-4) var(--space-4);
          justify-content: space-between;
          min-height: 100px;
        }

        .img {
          height: 67.5px;
          width: 60px;
          min-width: 60px;
        }

        .img :global(a > figure > img) {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .oos-product-content {
          display: flex;
          flex-direction: column;
          margin-left: var(--space-3);
          margin-right: var(--space-2);
          width: 100%;
        }

        .quantity {
          color: var(--clr-grey-4);
          margin-top: var(--space-1);
        }

        .action {
          width: 144px;
        }

        .action :global(button),
        .remove-product :global(button) {
          padding: 0 var(--space-4);
        }

        .remove-product :global(button) {
          border: none;
        }

        .remove-product {
          margin-right: var(--space-1);
        }

        @media (max-width: 800px) {
          .oos-products-container,
          .img-and-content {
            flex-direction: column;
          }

          .oos-products-container {
            width: 186px;
            height: 229px;
            padding: var(--space-3) var(--space-2);
            background-color: var(--clr-secondary-4);
            border-radius: var(--rd-2);
          }

          .oos-product-content {
            font-weight: var(--font-weight-base);
            text-align: center;
            margin: 0;
            padding-top: var(--space-5);
            max-height: 20px;
          }

          .title {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2; /* number of lines to show */
            line-clamp: 2;
            -webkit-box-orient: vertical;
          }

          .quantity {
            margin-top: var(--space-1);
            font-size: var(--font-size-2);
            font-weight: var(--font-weight-light);
          }

          .remove-product {
            position: absolute;
            top: 10px;
            right: 0;
            margin-right: 0;
          }

          .action :global(button) {
            background-color: white;
          }
        }
      `}</style>
    </>
  );
};

/**
 * Prevent re-render of all products in the shopping cart when it is updated
 */
const isEqual = (
  prevProps: IShoppingCartOOSProductRowProps,
  nextProps: IShoppingCartOOSProductRowProps,
): boolean => {
  const { shoppingCartOOSProduct: prevProduct } = prevProps;
  const { shoppingCartOOSProduct: nextProduct } = nextProps;

  return JSON.stringify(prevProduct) === JSON.stringify(nextProduct);
};

export default memo(ShoppingCartOOSProductRow, isEqual);
